"use client";

import React from "react";
import { GroupingWrapper } from "../../GroupingWrapper";
import { useUniversalEntityPageContext } from "../contexts/UniversalEntityPageContext";
import type { BaseEntity } from "../../UniversalCRMTable/types";

/**
 * EntityPageTable - Renders the table for an entity page
 *
 * Now consumes all data from UniversalEntityPageContext instead of receiving props.
 * This eliminates prop drilling from UniversalEntityPageContent.
 *
 * Memoized to prevent re-renders when parent context changes but table data hasn't.
 */
function EntityPageTableComponent<T extends BaseEntity>() {
  const context = useUniversalEntityPageContext<T>();

  // Destructure from context for clarity
  const { processedData, ftsIsLoading } = context.dataState;
  const { config } = context.pageConfig;
  const { handleGroupingExport, renderSimpleTable } = context.callbacks;
  const { searchTerm, setSearchTerm, tableFilters, setTableFilters } = context.filterState;
  const { handleEditItem, handleViewItem, handleOpenDetailModal } = context.handlers;

  return (
    <div className="h-full flex flex-col relative">
      <GroupingWrapper
        data={processedData}
        config={config}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={tableFilters}
        setFilters={setTableFilters}
        onExport={handleGroupingExport}
        onEdit={handleEditItem}
        onView={handleViewItem}
        onOpenDetailModal={handleOpenDetailModal}
        hasNextPage={context.dataState.hasNextPage}
        isFetchingNextPage={context.dataState.isFetchingNextPage}
        onLoadMore={context.dataState.loadMore}
      >
        {renderSimpleTable}
      </GroupingWrapper>

      {/* Show loading overlay only while waiting for initial FTS results */}
      {/* Hide as soon as we get ANY results (even if partial), let individual row */}
      {/* loading indicators (_isLoading on each row) show the full data fetch progress */}
      {ftsIsLoading && searchTerm && searchTerm.trim().length > 0 && processedData.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-gray-900/70 rounded z-50">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Searching...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export const EntityPageTable = React.memo(EntityPageTableComponent) as React.FC<{}>;
